<script setup>
import '@/__core__/style/style.scss'
import TabGeneral from './TabGeneral.vue'
import WrapperSmall from './WrapperSmall.vue'
import WrapperLarge from './WrapperLarge.vue'
import { computed, provide, ref, watch } from 'vue'
import useFetch from '@/__core__/composables/useFetch'
import Branding from '@/__v1.5__/components/branding/Branding.vue'
import { useWidgetContext } from '@/__v1.5__/composables/useWidgetContext'
import CaptionHeader from '@/__v1.5__/components/caption-headers/CaptionHeader.vue'
import { useFootballTeamProfileStore } from '@/__v1.5__/widgets/teamProfile/TeamProfile.store'
import FallbackScreenHandler from '@/__core__/components/fallbackScreens/FallbackScreenHandler.vue'
import { initLocalization } from '@/__core__/localization/localization'

const widgetId = 'team-profile'

const props = defineProps({
  languageIdentifier: {
    type: String,
    default: () => 'eng'
  },
  competitorId: {
    type: String,
    default: () => 'sr:competitor:44'
  },
  sportType: {
    type: String,
    default: () => 'football'
  },
  theme: {
    type: String,
    default: 'light'
  },
  size: {
    type: Number,
    default: 640
  },
  themeConfig: {
    type: Object,
    default: () => null
  }
})

/** @type {Ref<HTMLDivElement>} */
const containerRef = ref(null)

const teamProfileStore = useFootballTeamProfileStore()

const activeTabInHeader = ref('general')

const navigationTemplate = ref(new Array(3))

const { activeTheme, branded, containerClasses, langId, isLargeWidget } = useWidgetContext({ ...props, containerRef, widgetId })

const { status } = useFetch(async () => {
  await initLocalization(props.languageIdentifier)

  await teamProfileStore.fetchTeamProfileWidgetData({
    teamId: props.competitorId,
    langId: props.languageIdentifier
  })

  const { players = [] } = teamProfileStore.teamProfile?.team ?? {}
  navigationTemplate.value = [
    { title: 'sm_general', id: 'general' },
    ...(players.length ? [{ title: 'standings_team', id: 'team' }] : []),
    { title: 'sm_form', id: 'form' }
  ]
})

const containerBindings = computed(() => ({
  'e2e-id': 'szw-team-profile-widget',
  'theme-id': activeTheme.value,
  'lang-id': langId
}))


const captionHeaderProps = computed(() => ({
  title: teamProfileStore.teamProfile?.team?.name,
  subtitle: teamProfileStore.teamProfile?.team?.country?.code,
  logoUrl: teamProfileStore.teamProfile?.team?.logo?.medium,
  tabs: navigationTemplate.value,
  disableTabs: isLargeWidget.value,
  hasShadow: true,
  langId: langId
}))

function handleCategoryChange(newGroup) {
 activeTabInHeader.value = newGroup.id
}

const isSmallLayout = computed(() => (!isLargeWidget.value))

watch(() => isSmallLayout.value, () => activeTabInHeader.value = 'general')

provide('status', status)
</script>

<template>
  <div :class="containerClasses" ref="containerRef" v-bind="containerBindings">
    <fallback-screen-handler>
      <branding v-if="branded" :brand="activeTheme" />

      <div v-if="isSmallLayout" class="szw-flex szw-flex-col szw-max-w-[768px] szw-w-full szw-flex-1 szw-mx-auto szw-gap-1">
        <caption-header v-bind="captionHeaderProps" @tab-changed="handleCategoryChange" key="1"/>
        <WrapperSmall :activeTab="activeTabInHeader" :raw-data="teamProfileStore.teamProfile" :general-data="teamProfileStore.generalInfo" />
      </div>
      
      <div v-else class="szw-flex szw-w-full szw-gap-[16px] szw-max-w-[768px] szw-mx-auto szw-flex-1">
        <div class="szw-w-full szw-h-full szw-flex szw-flex-col szw-gap-2"> 
          <caption-header v-bind="captionHeaderProps" key="2"/>
          <TabGeneral :data="teamProfileStore.generalInfo"/>
        </div>
        
        <div class='szw-flex szw-w-full szw-gap-1 szw-flex-col'>
          <WrapperLarge :raw-data="teamProfileStore.teamProfile"/>
        </div>
      </div>
    </fallback-screen-handler>
  </div>
</template>
